import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { getAllPlanBundles } from '../utils/planBundleUtils';
import StripeUpgradeButton from './StripeUpgradeButton';
import { auth } from '../firebaseConfig';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function SubscriptionUpgradePanel({ 
  isOpen, 
  onClose, 
  onUpgrade,
  requiredLines = 1,
  currentSubscription = null
}) {
  const [selectedLines, setSelectedLines] = useState(Math.max(requiredLines, 1));
  const [selectedPeriod, setSelectedPeriod] = useState('Month');
  const [includeMessaging, setIncludeMessaging] = useState(true);
  const [planBundles, setPlanBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState(null);
  const [userUid, setUserUid] = useState(null);
  const [showingPaymentForm, setShowingPaymentForm] = useState(false);

  // Get current user's UID
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserUid(user.uid);
      }
    });

    return () => unsubscribe();
  }, []);

  // Load plan bundles on mount
  useEffect(() => {
    const loadPlanBundles = async () => {
      try {
        const allBundles = await getAllPlanBundles();
        
        // Filter out current plan and ArchivePlan (duplicate)
        const filteredBundles = allBundles.filter(bundle => {
          // Remove ArchivePlan as it's a duplicate of Bundle1LinesAnnual
          if (bundle.id === 'ArchivePlan') return false;
          
          // Remove current plan if user already has it
          if (currentSubscription?.bundleId === bundle.id) return false;
          
          return true;
        });
        
        setPlanBundles(filteredBundles);
        setLoading(false);
      } catch (err) {
        console.error('Error loading plan bundles:', err);
        setError('Failed to load pricing plans');
        setLoading(false);
      }
    };

    if (isOpen) {
      loadPlanBundles();
    }
  }, [isOpen, currentSubscription]);

  // Get current selected plan
  const getSelectedPlan = () => {
    if (includeMessaging) {
      return planBundles.find(bundle => 
        bundle.lines === selectedLines && 
        (bundle.period === 'Quarterly' || bundle.period === 'Quaterly') && // Handle typo in CSV
        bundle.isMessaging === true
      );
    } else {
      return planBundles.find(bundle => 
        bundle.lines === selectedLines && 
        bundle.period === selectedPeriod && 
        bundle.isMessaging === false
      );
    }
  };

  // Calculate potential savings for annual vs monthly
  const calculateSavings = () => {
    if (includeMessaging || selectedPeriod === 'Month') return null;
    
    const monthlyPlan = planBundles.find(bundle => 
      bundle.lines === selectedLines && 
      bundle.period === 'Month' && 
      bundle.isMessaging === false
    );
    
    const annualPlan = planBundles.find(bundle => 
      bundle.lines === selectedLines && 
      bundle.period === 'Annual' && 
      bundle.isMessaging === false
    );

    if (!monthlyPlan || !annualPlan) return null;

    const monthlyYearlyCost = parseFloat(monthlyPlan.displayPrice.replace('$', '')) * 12;
    const annualCost = parseFloat(annualPlan.displayPrice.replace('$', ''));
    const savings = monthlyYearlyCost - annualCost;

    return savings > 0 ? savings.toFixed(2) : null;
  };

  const handleUpgradeStart = () => {
    setUpgrading(true);
    setError(null);
  };

  const handleUpgradeComplete = async (result) => {
    try {
      // Call the original onUpgrade callback if needed for additional processing
      if (onUpgrade) {
        await onUpgrade(getSelectedPlan());
      }
      handleClose();
    } catch (err) {
      console.error('Post-upgrade processing error:', err);
      setError(err.message || 'Upgrade completed but failed to update the interface.');
    } finally {
      setUpgrading(false);
    }
  };

  const handleUpgradeError = (err) => {
    console.error('Upgrade error:', err);
    setError(err.message || 'Failed to upgrade subscription. Please try again.');
    setUpgrading(false);
  };

  const handlePaymentFormToggle = (isShowing) => {
    setShowingPaymentForm(isShowing);
  };

  // Reset all state when modal closes
  const handleClose = () => {
    setShowingPaymentForm(false);
    setUpgrading(false);
    setError(null);
    setSelectedLines(Math.max(requiredLines, 1));
    setSelectedPeriod('Month');
    setIncludeMessaging(true); // Reset to default
    onClose();
  };

  const selectedPlan = getSelectedPlan();
  const savings = calculateSavings();

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="mx-auto max-w-2xl w-full">
            {/* Liquid Glass Panel */}
            <div 
              className={`relative bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 flex flex-col ${
                showingPaymentForm ? 'min-h-[500px] max-h-none' : 'max-h-[85vh]'
              }`}
              style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 40px rgba(0,0,0,0.3)'
              }}
            >
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>

              {/* Header - Fixed */}
              <div className="px-8 pt-8 pb-6 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center gap-3 mb-2">
                  <SparklesIcon className="h-8 w-8 text-indigo-400" />
                  <h2 className="text-3xl font-light text-white">
                    {showingPaymentForm ? 'Payment Details' : 'Upgrade Your Plan'}
                  </h2>
                </div>
                <p className="text-white/60 text-lg">
                  {showingPaymentForm 
                    ? 'Enter your payment information to complete the upgrade.'
                    : 'You need more phone lines. Choose a plan that fits your needs.'
                  }
                </p>
                
                {/* Current Plan Display */}
                {currentSubscription && (
                  <div className="mt-4 p-4 rounded-xl bg-blue-500/20 border border-blue-400/30">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-blue-300 text-sm font-medium">Current Plan</span>
                    </div>
                    <p className="text-white font-medium">
                      {currentSubscription.bundleName || currentSubscription.name}
                    </p>
                    <p className="text-white/60 text-sm">
                      {currentSubscription.lines} line{currentSubscription.lines > 1 ? 's' : ''} • 
                      {currentSubscription.period === 'Quarterly' || currentSubscription.period === 'Quaterly' 
                        ? ' Every 3 Months' 
                        : ` ${currentSubscription.period}`}
                      {currentSubscription.isMessaging ? ' • With Messaging' : ''}
                    </p>
                  </div>
                )}
              </div>

              {loading ? (
                <div className="px-8 py-12 text-center flex-1">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400 mx-auto"></div>
                  <p className="text-white/60 mt-4">Loading plans...</p>
                </div>
              ) : (
                <>
                  {/* Scrollable Content */}
                  <div className={`px-8 py-6 ${showingPaymentForm ? 'overflow-y-auto flex-1' : 'overflow-y-auto flex-1 min-h-0'}`}>
                    {/* Error Display */}
                    {error && !showingPaymentForm && (
                      <div className="mb-6 p-4 rounded-xl bg-red-500/20 border border-red-500/30">
                        <p className="text-red-200 text-sm">{error}</p>
                      </div>
                    )}

                    {!showingPaymentForm ? (
                      <>
                        {/* Number of Lines Selector */}
                        <fieldset className="mb-8">
                          <legend className="block text-lg font-medium text-white mb-4">
                            How many phone lines do you need?
                          </legend>
                          <div className="grid grid-cols-5 gap-3">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((lines) => (
                              <button
                                key={lines}
                                onClick={() => setSelectedLines(lines)}
                                className={`
                                  h-12 rounded-xl border transition-all duration-200 font-medium
                                  ${selectedLines === lines
                                    ? 'bg-indigo-500/30 border-indigo-400/50 text-white'
                                    : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10 hover:text-white'
                                  }
                                `}
                              >
                                {lines}
                              </button>
                            ))}
                          </div>
                        </fieldset>

                        {/* Messaging Toggle */}
                        <div className="mb-8">
                          <div className="flex items-center justify-between p-6 rounded-xl bg-white/5 border border-white/10">
                            <div>
                              <h3 className="text-white font-medium text-lg">Include Messaging</h3>
                              <p className="text-white/60 text-sm">Send and receive text messages in the app</p>
                            </div>
                            <button
                              onClick={() => setIncludeMessaging(!includeMessaging)}
                              className={`
                                relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none
                                ${includeMessaging ? 'bg-indigo-500' : 'bg-gray-600'}
                              `}
                            >
                              <span
                                className={`
                                  pointer-events-none inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                  ${includeMessaging ? 'translate-x-6' : 'translate-x-0'}
                                `}
                              />
                            </button>
                          </div>
                        </div>

                        {/* Billing Period (only show if not messaging) */}
                        {!includeMessaging && (
                          <fieldset className="mb-8">
                            <legend className="block text-lg font-medium text-white mb-4">
                              Billing Period
                            </legend>
                            <div className="flex rounded-xl bg-white/5 border border-white/10 p-1">
                              {['Month', 'Annual'].map((period) => (
                                <button
                                  key={period}
                                  onClick={() => setSelectedPeriod(period)}
                                  className={`
                                    flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200
                                    ${selectedPeriod === period
                                      ? 'bg-indigo-500/30 text-white border border-indigo-400/30'
                                      : 'text-white/70 hover:text-white hover:bg-white/5'
                                    }
                                  `}
                                >
                                  {period}
                                  {period === 'Annual' && savings && (
                                    <span className="block text-green-400 text-xs mt-1">
                                      Save ${savings}/year
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </fieldset>
                        )}

                        {/* Selected Plan Summary */}
                        {selectedPlan && (
                          <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-400/30">
                            <h3 className="text-white font-medium text-lg mb-2">Selected Plan</h3>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-white text-xl font-light">{selectedPlan.name}</p>
                                <p className="text-white/60 text-sm">
                                  {selectedPlan.lines} line{selectedPlan.lines > 1 ? 's' : ''} • 
                                  {selectedPlan.period === 'Quarterly' || selectedPlan.period === 'Quaterly' 
                                    ? 'Every 3 Months' 
                                    : selectedPlan.period}
                                  {selectedPlan.isMessaging ? ' • With Messaging' : ''}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-white text-2xl font-light">{selectedPlan.displayPrice}</p>
                                <p className="text-white/60 text-sm">{selectedPlan.displayPricePerLine}/line</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full flex-1">
                        {/* Payment form will be rendered here by StripeUpgradeButton */}
                      </div>
                    )}
                  </div>

                  {/* Fixed Footer */}
                  <div className="px-8 py-6 border-t border-white/10 flex-shrink-0">
                                        {/* Stripe Upgrade Button */}
                    <div className="flex justify-center mb-4">
                      {userUid && (
                        <StripeUpgradeButton
                          uid={userUid}
                          selectedPlan={selectedPlan}
                          isUpgrading={upgrading}
                          onUpgradeStart={handleUpgradeStart}
                          onUpgradeComplete={handleUpgradeComplete}
                          onUpgradeError={handleUpgradeError}
                          onPaymentFormToggle={handlePaymentFormToggle}
                          stripePromise={stripePromise}
                        />
                      )}
                    </div>

                    {/* Fine Print */}
                    {!showingPaymentForm && (
                      <p className="text-white/40 text-xs text-center">
                        Your subscription will be updated immediately. You&apos;ll be charged the prorated amount for the current billing period.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

SubscriptionUpgradePanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpgrade: PropTypes.func.isRequired,
  requiredLines: PropTypes.number,
  currentSubscription: PropTypes.object
}; 